import type React from 'react'

import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material'
import { Link } from 'react-router-dom'

import {
  formatEventDate,
  formatFullDate,
  getRelativeTime,
  formatEventType,
  getMetadataString,
  LEVEL_CONFIG,
  ACTOR_CONFIG,
} from './event-log-utils'

import type { EventLog } from '../../api'

export type EventLogTableColumn = 'time' | 'event' | 'message' | 'level' | 'actor' | 'user' | 'link' | 'details'

type EventLogTableProps = {
  events: ReadonlyArray<EventLog>
  columns: ReadonlyArray<EventLogTableColumn>
  maxHeight?: number
  stickyHeader?: boolean
}

const formatMetadata = (event: EventLog): string | null => {
  if (!event.metadata) return null

  const previousStatus = getMetadataString(event.metadata, 'previousStatus')
  const newStatus = getMetadataString(event.metadata, 'newStatus')
  if (previousStatus && newStatus) {
    return `${previousStatus} \u2192 ${newStatus}`
  }

  return null
}

const TimeCell = ({ createdAt }: { createdAt: string }) => {
  const relative = getRelativeTime(createdAt)
  return (
    <Tooltip title={formatFullDate(createdAt)} arrow placement="top">
      <Box sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.4 }}>
          {formatEventDate(createdAt)}
        </Typography>
        {relative && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
            {relative}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

const EventTypeCell = ({ eventType }: { eventType: string }) => (
  <Tooltip title={eventType} arrow placement="top">
    <Chip
      label={formatEventType(eventType)}
      size="small"
      sx={{
        fontWeight: 500,
        fontSize: '0.75rem',
        bgcolor: 'grey.100',
        color: 'text.primary',
        maxWidth: 220,
        '& .MuiChip-label': {
          textTransform: 'capitalize',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  </Tooltip>
)

const LevelCell = ({ level }: { level: string }) => {
  const config = LEVEL_CONFIG[level as keyof typeof LEVEL_CONFIG] ?? LEVEL_CONFIG.info
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.6875rem',
        color: config.color,
        bgcolor: config.bg,
        border: `1px solid ${config.color}`,
        borderRadius: '6px',
        height: 22,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  )
}

const ActorCell = ({ actorType }: { actorType: string }) => {
  const config = ACTOR_CONFIG[actorType as keyof typeof ACTOR_CONFIG] ?? ACTOR_CONFIG.system
  const Icon = config.icon
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Icon sx={{ fontSize: 16, color: config.color }} />
      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
        {config.label}
      </Typography>
    </Box>
  )
}

const UserCell = ({ event }: { event: EventLog }) => {
  if (!event.userId || !event.user) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>
        &mdash;
      </Typography>
    )
  }

  return (
    <MuiLink
      component={Link}
      to={`/users/${encodeURIComponent(event.userId)}`}
      sx={{ textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 500 }}
    >
      {event.user.email}
    </MuiLink>
  )
}

const LinkCell = ({ event }: { event: EventLog }) => {
  const reviewId = getMetadataString(event.metadata, 'reviewId')
  const labOrderId = event.orderId ?? getMetadataString(event.metadata, 'labOrderId')

  const eventTypeLower = event.eventType.toLowerCase()
  const isQCEvent = eventTypeLower.includes('qc') || eventTypeLower.includes('review')

  if (reviewId && isQCEvent) {
    return (
      <MuiLink
        component={Link}
        to={`/ai-qc/review/${encodeURIComponent(reviewId)}`}
        sx={{ textDecoration: 'none', fontSize: '0.8125rem' }}
      >
        Review {reviewId.slice(0, 8)}
      </MuiLink>
    )
  }

  if (labOrderId) {
    return (
      <MuiLink
        component={Link}
        to={`/orders/${encodeURIComponent(labOrderId)}`}
        sx={{ textDecoration: 'none', fontSize: '0.8125rem' }}
      >
        Order {labOrderId.slice(0, 8)}
      </MuiLink>
    )
  }

  return (
    <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>
      &mdash;
    </Typography>
  )
}

const DetailsCell = ({ event }: { event: EventLog }) => {
  const details = formatMetadata(event)
  if (!details) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>
        &mdash;
      </Typography>
    )
  }
  return (
    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
      {details}
    </Typography>
  )
}

const MessageCell = ({ event }: { event: EventLog }) => {
  const trigger = getMetadataString(event.metadata, 'trigger')
  return (
    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
      {event.message}
      {trigger ? (
        <Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {' '}({trigger})
        </Typography>
      ) : null}
    </Typography>
  )
}

const COLUMN_LABELS: Record<EventLogTableColumn, string> = {
  time: 'Time',
  event: 'Event',
  message: 'Message',
  level: 'Level',
  actor: 'Actor',
  user: 'User',
  link: 'Link',
  details: 'Details',
}

const COLUMN_RENDERERS: Record<EventLogTableColumn, (event: EventLog) => React.ReactNode> = {
  time: (event) => <TimeCell createdAt={event.createdAt} />,
  event: (event) => <EventTypeCell eventType={event.eventType} />,
  message: (event) => <MessageCell event={event} />,
  level: (event) => <LevelCell level={event.level} />,
  actor: (event) => <ActorCell actorType={event.actorType} />,
  user: (event) => <UserCell event={event} />,
  link: (event) => <LinkCell event={event} />,
  details: (event) => <DetailsCell event={event} />,
}

export function EventLogTable({ events, columns, maxHeight, stickyHeader = false }: EventLogTableProps) {
  return (
    <TableContainer sx={{ maxHeight }}>
      <Table size="small" stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: 2,
                  borderColor: 'divider',
                  py: 1.5,
                }}
              >
                {COLUMN_LABELS[col]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <TableRow
              key={event.id}
              hover
              sx={{
                '&:last-child td': { borderBottom: 0 },
                transition: 'background-color 0.15s',
              }}
            >
              {columns.map((col) => (
                <TableCell key={col} sx={{ py: 1.25, verticalAlign: 'top' }}>
                  {COLUMN_RENDERERS[col](event)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
